class WrgInstagramFeed {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    /** @type {HTMLElement[]} */
    this.items = Array.from(root.querySelectorAll('[data-wrg-instagram-item]'));
    this.bindEvents();
  }

  bindEvents() {
    this.items.forEach((item) => {
      const video = /** @type {HTMLVideoElement|null} */ (
        item.querySelector('.wrg-instagram-feed__video')
      );
      const likeBtn = /** @type {HTMLButtonElement|null} */ (
        item.querySelector('[data-wrg-like]')
      );
      const playBadge = /** @type {HTMLElement|null} */ (
        item.querySelector('[data-wrg-play-badge]')
      );
      const overlay = /** @type {HTMLElement|null} */ (
        item.querySelector('.wrg-instagram-feed__overlay')
      );

      if (likeBtn) {
        likeBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          likeBtn.classList.toggle('is-liked');
          item.dataset.wrgLiked = likeBtn.classList.contains('is-liked') ? 'true' : 'false';
        });
        item.addEventListener('dblclick', (event) => {
          if (event.target && event.target.closest('a')) return;
          likeBtn.classList.add('is-liked');
          item.dataset.wrgLiked = 'true';
          likeBtn.dispatchEvent(new Event('wrg-liked-change'));
          this.spawnHeart(item, event);
        });
      }

      if (!video) return;

      /** @param {MouseEvent} event */
      item.addEventListener('click', (event) => {
        // Linke tıklamayı bozmamak için eğer gerçek <a> içindeysek çıkıyoruz
        const target = /** @type {HTMLElement|null} */ (event.target);
        if (target && target.closest('a')) return;
        if (!overlay) return;

        if (video.paused) {
          video.play();
          item.dataset.wrgPlaying = 'true';
          if (playBadge) playBadge.hidden = true;
          overlay.classList.add('is-playing');
        } else {
          video.pause();
          item.dataset.wrgPlaying = 'false';
          if (playBadge) playBadge.hidden = false;
          overlay.classList.remove('is-playing');
        }
      });
    });

    this.initShareButtons();
    this.formatCounts();
    this.observeItems();
    this.initCarouselNav();
  }

  /**
   * @param {HTMLElement} item
   * @param {MouseEvent} event
   */
  spawnHeart(item, event) {
    const heart = document.createElement('span');
    heart.className = 'wrg-instagram-feed__dblheart';
    const rect = item.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    heart.style.left = `${x}px`;
    heart.style.top = `${y}px`;
    item.appendChild(heart);
    setTimeout(() => heart.remove(), 600);
  }

  initShareButtons() {
    const shareBtns = this.root.querySelectorAll('[data-wrg-share]');
    shareBtns.forEach((btn) => {
      btn.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const linkEl = btn.closest('[data-wrg-instagram-item]')?.querySelector('.wrg-instagram-feed__link');
        const href = linkEl instanceof HTMLAnchorElement ? linkEl.href : window.location.href;
        try {
          await navigator.clipboard.writeText(href);
          this.showToast('Link kopyalandı');
        } catch (_) {
          this.showToast('Kopyalanamadı');
        }
      });
    });
  }

  formatCounts() {
    const formatter = (val) => {
      const num = Number(String(val).replace(/[^0-9.-]/g, ''));
      if (Number.isNaN(num)) return val;
      if (Math.abs(num) >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\\.0$/, '')}M`;
      if (Math.abs(num) >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\\.0$/, '')}K`;
      return String(num);
    };
    this.root.querySelectorAll('[data-wrg-count]').forEach((el) => {
      const raw = el.getAttribute('data-wrg-count') || el.textContent || '0';
      el.textContent = formatter(raw);
    });
  }

  showToast(message) {
    const toast = document.createElement('div');
    toast.className = 'wrg-instagram-feed__toast';
    toast.textContent = message;
    this.root.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 1500);
  }

  observeItems() {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const vid = el.querySelector('.wrg-instagram-feed__video');
          const badge = el.querySelector('[data-wrg-play-badge]');
          const overlay = el.querySelector('.wrg-instagram-feed__overlay');
          if (!(vid instanceof HTMLVideoElement)) return;
          if (entry.isIntersecting) {
            vid.play().catch(() => {});
            if (badge) badge.hidden = true;
            if (overlay) overlay.classList.add('is-playing');
          } else {
            vid.pause();
            if (badge) badge.hidden = false;
            if (overlay) overlay.classList.remove('is-playing');
          }
        });
      },
      {
        threshold: 0.4,
      }
    );

    this.items.forEach((item) => observer.observe(item));
  }

  initCarouselNav() {
    const grid = /** @type {HTMLElement|null} */ (this.root.querySelector('[data-wrg-carousel]'));
    if (!grid) return;
    const prev = /** @type {HTMLButtonElement|null} */ (this.root.querySelector('[data-wrg-scroll-prev]'));
    const next = /** @type {HTMLButtonElement|null} */ (this.root.querySelector('[data-wrg-scroll-next]'));
    const getStep = () => {
      const item = grid.querySelector('.wrg-instagram-feed__item');
      const gap = parseFloat(getComputedStyle(grid).columnGap || getComputedStyle(grid).gap || '0');
      return item ? item.getBoundingClientRect().width + gap : 300;
    };
    const scrollByDir = (dir) => {
      const step = getStep();
      grid.scrollBy({ left: dir * step, behavior: 'smooth' });
    };
    if (prev) prev.addEventListener('click', () => scrollByDir(-1));
    if (next) next.addEventListener('click', () => scrollByDir(1));
  }
}

function initWrgInstagramFeed() {
  document.querySelectorAll('.wrg-instagram-feed').forEach((section) => {
    const el = /** @type {HTMLElement & { __wrgInstagramFeed?: WrgInstagramFeed }} */ (section);
    if (!el.__wrgInstagramFeed) {
      el.__wrgInstagramFeed = new WrgInstagramFeed(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWrgInstagramFeed);
} else {
  initWrgInstagramFeed();
}
